<template>
  <home-floating-window
    :title="$tc('Cable', 1)"
    @close="closeCable"
    @changePosition="$emit('changeCableWindowsPosition', $event)"
    :pos-x="position.x"
    :pos-y="position.y"
  >
    <template v-slot:iconWindows>
      <CableSVG class="floating-container-svg" />
    </template>

    <template v-slot:objectDivWindows>
      <div class="floating-tool-content">
        <div class="cable-objects-div scroll-fire-fox">
          <div style="text-align: left; margin: 0">
            <treeselect
              v-model="cable.tier"
              :options="cableTierOptions"
              :normalizer="listElementNormalizer"
              :placeholder="null"
              :disabled="lineStringWKT.length > 0"
              disableFuzzyMatching
              :clearable="false"
            />
            <v-checkbox
              v-model="pathwaysWithIgnore"
              @change="$emit('filterPathwaysWithIgnore', pathwaysWithIgnore)"
              :label="$i18n.t('DontUsePathwaysIgnored')"
              color="accent"
              class="multi-selection-checkbox"
              :ripple="false"
            />

            <v-btn
              v-if="cableObjectsList.length > 0"
              small
              text
              class="clear-button"
              @click="clearCable"
            >
              <v-icon style="padding-right: 7px">clear_all</v-icon>
              {{ $t("ClearAll") }}
            </v-btn>
          </div>

          <div
            class="floating-objects-wrapper"
            :class="{ 'cable-direction-line': lineStringWKT.length > 1 }"
          >
            <CableSouthSVG
              v-show="lineStringWKT.length > 1"
              style="position: absolute; left: -6px; bottom: -4px;"
            />

            <div
              class="floating-object-selected"
              v-for="object in cableObjectsList"
              :key="object.uuid"
            >
              <TobyBoxSVG
                v-if="object.type.toLowerCase() === 'toby box'"
                class="floating-img"
              />
              <PrimaryNodeSVG
                v-if="object.type.toLowerCase() === 'primary node'"
                class="floating-img"
              />
              <VirtualSVG
                v-else-if="object.layerName.toLowerCase() === 'fc_virtual'"
                class="floating-img"
              />
              <img
                v-else-if="
                  object.type.toLowerCase() === 'closure' ||
                    object.type.toLowerCase() === 'cabinet'
                "
                class="floating-img"
                :src="
                  require(`@/assets/objectIcons/${object.type.toLowerCase() +
                    '_t' +
                    cable.tier}.png`)
                "
              />
              <img
                v-else-if="object.type.toLowerCase() === 'central office'"
                class="floating-img"
                :src="require(`@/assets/objectIcons/nro.png`)"
              />
              <img
                v-else
                class="floating-img"
                :src="
                  require(`@/assets/objectIcons/${object.type.toLowerCase()}.png`)
                "
              />

              <div
                class="floating-between-objects"
                v-if="object.uuid !== cableObjectsList[0].uuid"
                :style="{ 'border-color': getBorderColorTier() }"
              />

              <span style="text-align: left; padding-right: 5px">
                {{ objectIdentification(object) }}
              </span>

              <div
                class="floating-hover-options"
                @click="openCableObjectMenu($event, object)"
              >
                <v-icon>more_vert</v-icon>
              </div>
            </div>
          </div>
          <HighlightedBox
            :bad-object="badObject"
            :is-visible="true"
            :message="cableText"
            :state="state"
          />
        </div>

        <home-window-button
          :label="$t('Create')"
          :disabled="!cable.creatable"
          @buttonClick="createCable"
        >
          <md-tooltip
            v-show="!cable.creatable"
            class="cable-create-hover"
            md-direction="bottom"
          >
            {{ getTooltipMessage() }}
          </md-tooltip>
        </home-window-button>
      </div>

      <window-object-menu
        v-if="cableObjectMenu.open"
        :event="cableObjectMenu.event"
        :object="cableObjectMenu.object"
        :actionFrom="selectedToolObjectLayer"
        @updateGeoServer="updateGeoServer"
        @closeMenu="closeCableObjectMenu"
        @openForm="openFormFromCableObjectMenu"
        @openDetailForm="openFormFromCableObjectMenu('detail')"
        @removeFromCable="removeObjectFromCable"
      />

      <app-dialog-box
        :open="clearDialog"
        :title="$t('CableCreationClear')"
        :content="
          $t('CableCreationWarning') + '\n\n' + $t('CableCreationConfirmation')
        "
        :main-action-label="$t('No')"
        @mainAction="clearDialog = false"
        :secondary-action-label="$t('Yes')"
        @secondaryAction="clearCable"
      />

      <app-dialog-box
        :open="closeDialog"
        :title="$t('CableCreationLeave')"
        :content="
          $t('CableCreationWarning') + '\n\n' + $t('CableCreationConfirmation')
        "
        :main-action-label="$t('No')"
        @mainAction="closeDialog = false"
        :secondary-action-label="$t('Yes')"
        @secondaryAction="closeCable"
      />
    </template>
  </home-floating-window>
</template>

<script>
import { bus } from "@/main.js";
import { mapActions } from "vuex";
import CableSVG from "@/assets/cable_menu/cable_window.svg";
import WindowHeaderButton from "@/components/_Atoms/WindowHeaderButton";
import CableSouthSVG from "@/assets/cable_menu/cable_south.svg";
import TobyBoxSVG from "@/assets/objectIcons/tobybox.svg";
import PrimaryNodeSVG from "@/assets/objectIcons/primary node.svg";
import HomeWindowButton from "../HomeWindowButton";
import WindowObjectMenu from "../ObjectMenu/WindowObjectMenu";
import HomeFloatingWindow from "./HomeFloatingWindow";
import { Stroke } from "ol/style";
import { GeoJSON } from "ol/format";
import { Style } from "ol/style.js";
import { mixin as clickaway } from "vue-clickaway";
import AppDialogBox from "@/components/_Molecules/AppDialogBox";
import HighlightedBox from "@/components/_Molecules/HighlightedBox";
import { listsMixin } from "@/mixins/listsMixin";
import VirtualSVG from "@/assets/objectIcons/virtual.svg";

export default {
  name: "CableWindow",

  mixins: [clickaway, listsMixin],

  components: {
    HighlightedBox,
    HomeFloatingWindow,
    AppDialogBox,
    CableSVG,
    WindowHeaderButton,
    CableSouthSVG,
    HomeWindowButton,
    WindowObjectMenu,
    TobyBoxSVG,
    PrimaryNodeSVG,
    VirtualSVG,
  },

  props: {
    cable: {
      type: Object
    },
    lineStringWKT: {
      type: Array
    },
    vectorSource: {},

    position: Object,

    filterPathwaysWithIgnore: Boolean
  },

  data() {
    return {
      cableText: "",
      temporaryCables: [],

      cableObjectMenu: {
        open: false,
        event: null,
        object: null
      },

      cableObjectsList: [],

      badObject: false,

      clearDialog: false,
      closeDialog: false,

      state: "select",

      pathwaysWithIgnore: false
    };
  },

  computed: {
    cableTierOptions() {
      return this.$store.getters.optionLists_cableType;
    },

    selectedToolObjectLayer() {
      return this.$store.getters.tools_selectedObjectLayer;
    },

    objectIdentifiers() {
      return this.$store.getters.selectedObjectIdentifiers;
    }
  },

  watch: {
    "cable.tier"() {
      this.setHighlightedBoxMessage();
    },

    cableObjectsList() {
      this.setHighlightedBoxMessage();
    },

    filterPathwaysWithIgnore() {
      this.pathwaysWithIgnore = this.filterPathwaysWithIgnore;
    }
  },

  mounted() {
    this.pathwaysWithIgnore = this.filterPathwaysWithIgnore;
    this.setHighlightedBoxMessage();
    bus.$on("highlight-cable-box", () => this.highlightCableBox());
    bus.$on("add-node-to-cable", () => this.addNode());
    bus.$on("end-cable-creation", () => {
      this.cableObjectsList = [];
    });

    bus.$on("escape-cable-window", () => this.escapeCableWindow());
    bus.$on("deactivate-cable-creation", () => this.tryCloseCable());
  },

  destroyed() {
    bus.$off("highlight-cable-box");
    bus.$off("add-node-to-cable");
    bus.$off("end-cable-creation");
    bus.$off("escape-cable-window");
    bus.$off("deactivate-cable-creation");
  },

  methods: {
    ...mapActions([
      "BEST_ROUTE",
      "fetchObjectByUUID",
      "UPDATE_SELECTED_OBJECT_IDENTIFIERS",
      "UPDATE_SELECTED_OBJECT_DATA"
    ]),

    escapeCableWindow() {
      if (this.cableObjectsList.length > 1) {
        this.clearDialog = true;
      } else if (this.cableObjectsList.length === 1) {
        this.clearCable();
      } else {
        this.closeCable();
      }
    },

    clearCable() {
      this.clearDialog = false;
      this.cable.creatable = false;
      this.cableObjectsList = [];
      this.$emit("clear");
    },

    tryCloseCable() {
      if (this.cableObjectsList.length > 1) {
        this.closeDialog = true;
      } else {
        this.closeCable();
      }
    },

    closeCable() {
      this.close = false;
      this.$emit("close");
    },

    openCableObjectMenu(ev, object) {
      setTimeout(() => {
        this.cableObjectMenu.open = true;
        this.cableObjectMenu.event = ev;
        this.cableObjectMenu.object = object;
      }, 0);
    },

    createCable() {
      if (this.lineStringWKT.length > 1 && this.cable.creatable) {
        this.$emit("openForm", "fc_cable", "create");
      }
    },

    objectIdentification(object) {
      let label = "";
      if (object.label) {
        label += object.label;
      } else {
        label += object.id;
      }
      return label;
    },

    addNode() {
      //highlight ao ponto
      let wkt = "";
      this.$emit("add-node", this.objectIdentifiers.coordinates, result => {
        wkt = result;
      });
      let object = {
        type: this.objectIdentifiers.objectType,
        uuid: this.objectIdentifiers.uuid,
        text_geom: wkt,
        layerName: this.objectIdentifiers.layerName
      };

      if (
        !this.lineStringWKT.find(
          point => point.uuid === this.objectIdentifiers.uuid
        )
      ) {
        if (
          object.type === "technical_local_olt" ||
          object.type === "technical_local_fdp"
        ) {
          object.type = "technical_local";
        }

        this.lineStringWKT.push(object);

        this.cableObjectsList.push(this.objectIdentifiers);
        this.scrollCableWindow();
        this.findBestRoute();
      }
    },

    scrollCableWindow() {
      this.$nextTick(() => {
        let container = this.$el.querySelector(".cable-objects-div");
        container.scrollTop = container.scrollHeight;
      });
    },

    findBestRoute() {
      if (this.cableObjectsList.length < 1) {
        return;
      }
      if (this.cableObjectsList.length === 1) {
        this.vectorSource.clear();
        this.cable.creatable = false;
        let originType = this.cableObjectsList[0].type;
        if (
          originType === "Building" ||
          originType === "Pole" ||
          originType === "Manhole"
        ) {
          this.lineStringWKT.pop();
          this.cableObjectsList.pop();
          this.$toasted.global.toast_error(originType + " can't be an origin");
        }
        return;
      }
      if (this.lineStringWKT.length > 1) {
        this.BEST_ROUTE({
          node: this.lineStringWKT,
          projection: 3857,
          filterPathwaysWithIgnore: this.filterPathwaysWithIgnore
        })
          .then(resp => {
            this.vectorSource.clear();

            let pathways = resp.data.data.pathways;
            this.cable.pathways = pathways;

            if (Array.isArray(pathways)) {
              for (const pathway of pathways) {
                let features = new GeoJSON().readFeatures(pathway.pw_geo_json, {
                  featureProjection: "EPSG:3857"
                });

                let feature = features[0];
                let style = this.defineCableStyle();
                feature.setStyle(style);
                this.temporaryCables.push(feature);
                this.vectorSource.addFeature(feature);
              }

              this.updateGeoServer();
            }

            this.cable.creatable = !(
              this.lineStringWKT[this.lineStringWKT.length - 1].type ===
                "technical_point" ||
              this.lineStringWKT[0].type === "technical_point" ||
              this.lineStringWKT[0].type === "building"
            );
          })
          .catch(error => {
            this.lineStringWKT.pop();
            this.cableObjectsList.pop();
            this.$toasted.global.toast_error(error.response.data.data);
          });
      }
    },

    defineCableStyle() {
      let color;
      switch (this.cable.tier) {
        case "1":
          color = "#CA002A";
          break;
        case "2":
          color = "#79CD54";
          break;
        case "3":
          color = "#7698dd";
          break;
        case "4":
          color = "#ff8c00";
          break;
      }
      return new Style({
        stroke: new Stroke({ color: color, width: 3 })
      });
    },

    updateGeoServer() {
      this.$emit("updateGeoserver");
    },

    closeCableObjectMenu() {
      this.cableObjectMenu.open = false;
    },

    openFormFromCableObjectMenu(action) {
      this.closeCableObjectMenu();
      this.getObjectDataFromCableMenu(this.cableObjectMenu.object, action);
    },

    getObjectDataFromCableMenu(objectIdentifiers, formType) {
      let endpoint = objectIdentifiers.endpoint;
      let uuid = objectIdentifiers.uuid;
      let fields = this.getFields(endpoint);
      let layerName = objectIdentifiers.layerName;

      this.fetchObjectByUUID({
        endpoint: endpoint,
        objectUUID: uuid,
        fieldsToInclude: fields
      })
        .then(resp => {
          this.UPDATE_SELECTED_OBJECT_IDENTIFIERS(objectIdentifiers);
          this.UPDATE_SELECTED_OBJECT_DATA(resp.data.data[0]);
          if (formType === "detail") {
            this.$emit("openForm", "Detail", "edit");
          } else {
            this.$emit("openForm", layerName, formType);
          }
        })
        .catch(error => {
          this.$toasted.global.toast_error(error.response.data.data);
          console.log(error);
        });
    },

    removeObjectFromCable(uuid) {
      this.closeCableObjectMenu();
      let index = this.cableObjectsList.findIndex(point => point.uuid === uuid);
      if (index > -1) {
        if (this.cableObjectsList.length < 2) {
          this.lineStringWKT.splice(index, 1);
          this.cableObjectsList.splice(index, 1);
          return;
        }
        let origin = this.cableObjectsList[0];
        let secondObjectType = this.cableObjectsList[1].type;
        if (
          origin.uuid === uuid &&
          (secondObjectType === "Pole" ||
            secondObjectType === "Manhole" ||
            (secondObjectType === "Building") |
              (secondObjectType === "Technical Local FDP"))
        ) {
          this.$toasted.global.toast_error(
            secondObjectType + " can't be an origin"
          );
          return;
        }
        this.lineStringWKT.splice(index, 1);
        this.cableObjectsList.splice(index, 1);
        this.findBestRoute();
      }
    },

    getFields(layer) {
      switch (layer) {
        case "closures":
          return "technicalpoint,fiberterminalbox";
        case "technicalpoints":
          return "images";
        case "buildings":
          return "images,technicalsite,technicallocals";
        case "site":
        case "technicallocals":
          return "technicalsite";
        case "technicalsites":
          return "technicallocals,images";
        default:
          return "";
      }
    },

    highlightCableBox() {
      this.badObject = true;
      setTimeout(() => {
        this.badObject = false;
      }, 550);
    },

    getBorderColorTier() {
      switch (this.cable.tier) {
        case "1":
          return "#CA002A";
        case "2":
          return "#79CD54";
        case "3":
          return "#7698dd";
        case "4":
          return "#ff8c00";
      }
    },

    setHighlightedBoxMessage() {
      this.state = "selected";
      switch (this.cable.tier) {
        case "1":
          this.setDropTierMessage();
          break;
        case "2":
          this.setDistributionTierMessage();
          break;
        case "3":
          this.setFeederTierMessage();
          break;
        case "4":
          this.setBackboneTierMessage();
          break;
      }
    },

    setDropTierMessage() {
      if (this.lineStringWKT.length === 0) {
        this.cableText = this.$t("SelectDropFirstNode");
      } else if (
        this.lineStringWKT[this.lineStringWKT.length - 1].type &&
        this.lineStringWKT[this.lineStringWKT.length - 1].type !== "building"
      ) {
        if (this.lineStringWKT[this.lineStringWKT.length - 1].layerName === "fc_technical_local_mdu") {
          this.cableText = this.$t("SelectSDUOrMDUTechLocal");
        } else {
          this.cableText = this.$t("SelectBuildingPoleOrManhole");
        }
      } else {
        this.cableText = this.$t("AfterSelectBuilding");
        this.state = "warning";
      }
    },

    setDistributionTierMessage() {
      if (this.lineStringWKT.length === 0) {
        this.cableText = this.$t("SelectFDCOrClosure");
      } else if (
        this.lineStringWKT[this.lineStringWKT.length - 1].type &&
        this.lineStringWKT[this.lineStringWKT.length - 1].type !==
          "Technical Local MDU"
      ) {
        this.cableText = this.$t("SelectClosureTlMduPoleOrManhole");
      } else {
        this.cableText = this.$t("AfterSelectTechLocalMDU");
        this.state = "warning";
      }
    },

    setFeederTierMessage() {
      if (this.lineStringWKT.length === 0) {
        this.cableText = this.$t("SelectCOOrFDCOrClosure");
      } else if (
        this.lineStringWKT[this.lineStringWKT.length - 1].type &&
        this.lineStringWKT[this.lineStringWKT.length - 1].type !==
          "Technical Local FDP"
      ) {
        this.cableText = this.$t("SelectFDCOrClosureCabinetTLPoleOrManhole");
      } else {
        this.cableText = this.$t("AfterSelectTechLocalFDP");
        this.state = "warning";
      }
    },

    setBackboneTierMessage() {
      if (this.lineStringWKT.length === 0) {
        this.cableText = this.$t("SelectOLTClosure");
      } else {
        this.cableText = this.$t("SelectOLTClosureCabinetPoleOrManhole");
      }
    },

    getTooltipMessage() {
      switch (this.cable.tier) {
        case "1":
          return this.$i18n.t("DropCableEndObject");
        case "2":
          return this.$i18n.t("DistributionCableEndObject");
        case "3":
          return this.$i18n.t("TransportCableEndObject");
        case "4":
          return this.$i18n.t("BackboneCableEndObject");
      }
    }
  }
};
</script>

<style scoped>
.clear-button {
  margin: 15px 0 0;
  color: #515358;
  font-size: 12px;
  text-transform: none;
  left: 4px;
}

.cable-objects-div {
  min-height: 120px;
  max-height: 26vh;
  overflow: auto;
  padding: 5px 10px;
  scroll-behavior: smooth;
}

.cable-create-hover {
  height: fit-content;
  padding: 2px 5px;
  color: var(--secondary-lighter);
  background-color: var(--primary-normal);
  margin-top: -8px;
  z-index: 100;
}

.cable-direction-line {
  border-left: 1px dashed var(--primary-highlight);
}
</style>



// WEBPACK FOOTER //
// src/components/Home/Content/ObjectWindow/CableWindow.vue